import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Divider,
  Chip,
  ButtonGroup,
  CircularProgress,
  Skeleton,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Clear as ClearIcon,
  CheckCircle as CheckCircleIcon,
  Badge as BadgeIcon,
} from '@mui/icons-material';
import { loadPatients } from '../../BaseURL';
import axios from 'axios';
import Swal from 'sweetalert2';
import PatientVitals from './PatientVitals';
import PatientIDCard from '../../Record/Patientidcard';

import DeviceThermostatOutlinedIcon from '@mui/icons-material/DeviceThermostatOutlined';

// ─── Helper: did this patient register today? ────────────────────────────────
// Handles: YYYY-MM-DD, YYYY-MM-DD HH:mm:ss, DD-MM-YYYY, DD/MM/YYYY, MM/DD/YYYY
const isRegisteredToday = (dateStr) => {
  if (!dateStr) return false;

  const today = new Date();
  const ty = today.getFullYear();
  const tm = today.getMonth() + 1; // 1-based
  const td = today.getDate();

  const s = String(dateStr).trim();

  // YYYY-MM-DD or YYYY-MM-DD HH:mm:ss  (MySQL default)
  const isoMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return (
      parseInt(isoMatch[1]) === ty &&
      parseInt(isoMatch[2]) === tm &&
      parseInt(isoMatch[3]) === td
    );
  }

  // DD-MM-YYYY or DD/MM/YYYY
  const dmyMatch = s.match(/^(\d{2})[-\/](\d{2})[-\/](\d{4})/);
  if (dmyMatch) {
    return (
      parseInt(dmyMatch[3]) === ty &&
      parseInt(dmyMatch[2]) === tm &&
      parseInt(dmyMatch[1]) === td
    );
  }

  // Fallback: let JS parse it
  try {
    const reg = new Date(s);
    if (!isNaN(reg)) {
      return (
        reg.getFullYear() === ty &&
        reg.getMonth() + 1 === tm &&
        reg.getDate() === td
      );
    }
  } catch (_) {}

  return false;
};

const Home = ({ onAddNewPatient, onEditPatient }) => {

  const [searchBy, setSearchBy] = useState({
    opdNo: true,
    name: false,
    insuranceNo: false,
    phoneNo: false,
    fingerPrint: false
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSearchTerm, setActiveSearchTerm] = useState('');
  const [entriesPerPage, setEntriesPerPage] = useState(20);
  const [activeFilter, setActiveFilter] = useState('today');
  const [isLoading, setIsLoading] = useState(true);
  const [allPatientData, setallPatientData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  
  const [selectedPatients, setSelectedPatients] = useState(new Set());
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [openPatient, setOpenPatient] = useState(false);
  const [vitalsInfo, setVitalsInfo] = useState(null);

  // ── ID Card state ──────────────────────────────────────────────────────────
  const [openIDCard, setOpenIDCard] = useState(false);
  const [idCardPatient, setIdCardPatient] = useState(null);

  // Read company info once from localStorage
  const companyInfo = (() => {
    try { return JSON.parse(localStorage.getItem('companyInfo') || '{}'); }
    catch { return {}; }
  })();

  // Loading skeleton component for table rows
  const LoadingSkeleton = () => (
    <>
      {[...Array(5)].map((_, index) => (
        <TableRow key={index}>
          <TableCell><Skeleton variant="rectangular" width={20} height={20} /></TableCell>
          <TableCell><Skeleton variant="text" width={60} /></TableCell>
          <TableCell><Skeleton variant="text" width={120} /></TableCell>
          <TableCell><Skeleton variant="rectangular" width={60} height={24} sx={{ borderRadius: 1 }} /></TableCell>
          <TableCell><Skeleton variant="text" width={40} /></TableCell>
          <TableCell><Skeleton variant="text" width={80} /></TableCell>
          <TableCell><Skeleton variant="text" width={100} /></TableCell>
          <TableCell><Skeleton variant="text" width={80} /></TableCell>
          <TableCell><Skeleton variant="text" width={90} /></TableCell>
          <TableCell><Skeleton variant="text" width={80} /></TableCell>
          <TableCell sx={{ textAlign: 'center' }}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
              <Skeleton variant="circular" width={32} height={32} />
              <Skeleton variant="circular" width={32} height={32} />
              <Skeleton variant="circular" width={32} height={32} />
            </Box>
          </TableCell>
        </TableRow>
      ))}
    </>
  );

  useEffect(() => {
    AllPatient();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter, entriesPerPage, activeSearchTerm]);

  const AllPatient = async () => {
    try {
      setIsLoading(true);
      const activeSearchFields = Object.keys(searchBy).filter(k => searchBy[k]).join(',');
      const params = new URLSearchParams({
        filter: activeFilter,
        limit: entriesPerPage,
        search: activeSearchTerm,
        searchBy: activeSearchFields
      });
      const response = await axios.get(`${loadPatients}?${params}`);
      if (response.data.success) {
        setallPatientData(response.data.results);
        setTotalRecords(response.data.total || response.data.results.length);
      }
    } catch (error) {
      await Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || 'Failed Load Data',
        icon: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchByChange = (field) => {
    setSearchBy(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSearch = () => { setActiveSearchTerm(searchTerm); };
  const handleClearSearch = () => { setSearchTerm(''); setActiveSearchTerm(''); };
  const handleSearchKeyPress = (e) => { if (e.key === 'Enter') handleSearch(); };

  const handleRowSelect = (patient) => {
    setSelectedPatient(patient);
    const next = new Set(selectedPatients);
    selectedPatients.has(patient.id) ? next.delete(patient.id) : next.add(patient.id);
    setSelectedPatients(next);
  };

  const handleClearSelection = () => { setSelectedPatients(new Set()); setSelectedPatient(null); };

  const handleSelectAll = () => {
    if (selectedPatients.size === allPatientData.length) {
      handleClearSelection();
    } else {
      setSelectedPatients(new Set(allPatientData.map(p => p.id)));
    }
  };

  const handleEditRecord = (patient) => {
    if (onEditPatient) onEditPatient(patient);
  };

  const handleOpenVitals = (patient) => {
    setVitalsInfo(patient);
    setOpenPatient(true);
  };

  const handleClosePatient = () => { setOpenPatient(false); setVitalsInfo(null); };
  const handleAddNewPatient = () => { if (onAddNewPatient) onAddNewPatient(); };

  // ── ID Card handlers ───────────────────────────────────────────────────────
  const handleOpenIDCard = (patient) => { setIdCardPatient(patient); setOpenIDCard(true); };
  const handleCloseIDCard = () => { setOpenIDCard(false); setIdCardPatient(null); };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1e293b', mb: 1 }}>
            Medical records
          </Typography>
          <Typography variant="subtitle1" sx={{ color: '#64748b' }}>
            Welcome to the Hospital Records System. Below is the list of your patients:
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddNewPatient}
          disabled={isLoading}
          sx={{
            backgroundColor: '#059669', color: 'white',
            fontSize: '16px', fontWeight: 600, py: 2, px: 4,
            borderRadius: 2, textTransform: 'none',
            boxShadow: '0 4px 16px rgba(5,150,105,0.3)',
            '&:hover': { backgroundColor: '#047857', boxShadow: '0 6px 20px rgba(5,150,105,0.4)' },
            '&:disabled': { backgroundColor: '#9ca3af', boxShadow: 'none' },
          }}
        >
          Add New Patient
        </Button>
      </Box>

      {/* Selection alert */}
      {selectedPatients.size > 0 && (
        <Alert
          severity="info"
          sx={{ mb: 3, borderRadius: 2, '& .MuiAlert-message': { display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' } }}
          action={
            <Button color="inherit" size="small" onClick={handleClearSelection} startIcon={<ClearIcon />} sx={{ textTransform: 'none' }}>
              Clear Selection
            </Button>
          }
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon fontSize="small" />
            <Typography>{selectedPatients.size} patient{selectedPatients.size !== 1 ? 's' : ''} selected</Typography>
          </Box>
        </Alert>
      )}

      {/* Search Section */}
      <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 4px 16px rgba(0,0,0,0.05)' }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 3, color: '#374151', fontWeight: 600 }}>
            Search & Filter Patients
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {[
                { key: 'opdNo', label: 'OPD No.' },
                { key: 'name', label: 'Name' },
                { key: 'insuranceNo', label: 'Insurance No.' },
                { key: 'phoneNo', label: 'Phone No.' },
              ].map(item => (
                <FormControlLabel
                  key={item.key}
                  control={<Checkbox checked={searchBy[item.key]} onChange={() => handleSearchByChange(item.key)} disabled={isLoading} sx={{ color: '#059669' }} />}
                  label={item.label}
                  sx={{ '& .MuiFormControlLabel-label': { color: '#374151', fontWeight: 500 } }}
                />
              ))}
            </Box>

            <Divider orientation="vertical" flexItem sx={{ height: 40, alignSelf: 'center' }} />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="subtitle2" sx={{ color: '#374151', fontWeight: 600, whiteSpace: 'nowrap' }}>
                Filter by Period:
              </Typography>
              <ButtonGroup variant="contained" sx={{ boxShadow: 'none' }}>
                {[
                  { key: 'today', label: 'Today' },
                  { key: 'week', label: 'This Week' },
                  { key: 'month', label: 'This Month' },
                  { key: 'year', label: 'This Year' },
                  { key: 'all', label: 'All' }
                ].map(filter => (
                  <Button
                    key={filter.key}
                    onClick={() => setActiveFilter(filter.key)}
                    disabled={isLoading}
                    sx={{
                      backgroundColor: activeFilter === filter.key ? '#059669' : '#e2e8f0',
                      color: activeFilter === filter.key ? 'white' : '#64748b',
                      fontWeight: 600, textTransform: 'none', px: 2, py: 1, fontSize: '0.875rem',
                      '&:hover': { backgroundColor: activeFilter === filter.key ? '#059669' : '#cbd5e1' },
                      '&:disabled': {
                        backgroundColor: activeFilter === filter.key ? '#9ca3af' : '#f1f5f9',
                        color: activeFilter === filter.key ? '#e5e7eb' : '#9ca3af',
                      },
                    }}
                  >
                    {filter.label}
                  </Button>
                ))}
              </ButtonGroup>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              variant="outlined"
              placeholder="Search patients..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              disabled={isLoading}
              sx={{ flexGrow: 1, minWidth: 300, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              InputProps={{
                endAdornment: searchTerm && (
                  <IconButton size="small" onClick={handleClearSearch} disabled={isLoading} sx={{ color: '#64748b' }}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                )
              }}
            />
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              disabled={isLoading}
              sx={{
                backgroundColor: '#059669', color: 'white', borderRadius: 2, px: 3, py: 1.5, fontWeight: 600, textTransform: 'none',
                '&:hover': { backgroundColor: '#047857', boxShadow: '0 4px 12px rgba(5,150,105,0.3)' },
                '&:disabled': { backgroundColor: '#9ca3af', color: '#e5e7eb' },
              }}
            >
              Search
            </Button>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ color: '#374151', fontWeight: 500 }}>Show</Typography>
              <FormControl size="small" sx={{ minWidth: 80 }}>
                <Select value={entriesPerPage} onChange={e => setEntriesPerPage(e.target.value)} disabled={isLoading} sx={{ borderRadius: 2 }}>
                  {[10, 20, 50, 100, 500, 1000].map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                </Select>
              </FormControl>
              <Typography sx={{ color: '#374151', fontWeight: 500 }}>entries</Typography>
            </Box>
          </Box>

          {activeSearchTerm && (
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ color: '#64748b' }}>Searching for:</Typography>
              <Chip label={activeSearchTerm} onDelete={handleClearSearch} size="small" sx={{ backgroundColor: '#dbeafe', color: '#1e40af', fontWeight: 500 }} />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Patient Table */}
      <Card sx={{ borderRadius: 2, boxShadow: '0 4px 16px rgba(0,0,0,0.05)' }}>
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedPatients.size > 0 && selectedPatients.size < allPatientData.length}
                    checked={allPatientData.length > 0 && selectedPatients.size === allPatientData.length}
                    onChange={handleSelectAll}
                    disabled={isLoading || allPatientData.length === 0}
                    sx={{ color: '#059669' }}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#374151' }}>Patient ID</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#374151' }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#374151' }}>Gender</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#374151' }}>Age</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#374151' }}>Insurance #</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#374151' }}>Address</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#374151' }}>Telephone</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#374151' }}>Occupation</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#374151' }}>Visit Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#374151', textAlign: 'center' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <LoadingSkeleton />
              ) : allPatientData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary" sx={{ fontSize: '16px' }}>
                      {activeSearchTerm ? 'No patients found matching your search criteria' : 'No patient records found for this period'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                allPatientData.map((patient) => {
                  const canPrintID = isRegisteredToday(patient.reg_date);
                  return (
                    <TableRow
                      key={patient.id}
                      selected={selectedPatients.has(patient.id)}
                      sx={{
                        '&:hover': { backgroundColor: '#f8fafc' },
                        '&:nth-of-type(even)': { backgroundColor: '#fafbfc' },
                        '&.Mui-selected': {
                          backgroundColor: '#ecfdf5 !important',
                          '&:hover': { backgroundColor: '#d1fae5 !important' },
                        },
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox checked={selectedPatients.has(patient.id)} onChange={() => handleRowSelect(patient)} sx={{ color: '#059669' }} />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#3b82f6' }}>{patient.id}</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{patient.fullName}</TableCell>
                      <TableCell>
                        <Chip
                          label={patient.gender}
                          size="small"
                          sx={{
                            backgroundColor: patient.gender === 'Male' || patient.gender === 'MALE' ? '#dbeafe' : '#fce7f3',
                            color: patient.gender === 'Male' || patient.gender === 'MALE' ? '#1d4ed8' : '#be185d',
                            fontWeight: 500,
                          }}
                        />
                      </TableCell>
                      <TableCell>{patient.age}</TableCell>
                      <TableCell>{patient.Insurance}</TableCell>
                      <TableCell>{patient.res}</TableCell>
                      <TableCell>{patient.tel}</TableCell>
                      <TableCell>{patient.occ}</TableCell>
                      <TableCell sx={{ color: '#059669', fontWeight: 500 }}>{patient.reg_date}</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>

                          {/* Vitals */}
                          <IconButton
                            onClick={() => handleOpenVitals(patient)}
                            sx={{
                              backgroundColor: '#fef3f2', color: '#dc2626',
                              '&:hover': { backgroundColor: '#fee2e2', color: '#b91c1c' },
                              borderRadius: 2, p: 1,
                            }}
                            title={`View ${patient.fullName}'s vitals`}
                          >
                            <DeviceThermostatOutlinedIcon fontSize="small" />
                          </IconButton>

                          {/* Edit */}
                          <IconButton
                            onClick={() => handleEditRecord(patient)}
                            sx={{
                              backgroundColor: '#f0f9ff', color: '#0ea5e9',
                              '&:hover': { backgroundColor: '#e0f2fe', color: '#0284c7' },
                              borderRadius: 2, p: 1,
                            }}
                            title={`Edit ${patient.fullName}'s record`}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>

                          {/* ── ID Card (only for today's registrations) ── */}
                          <Tooltip
                            title={canPrintID ? `Print ID card for ${patient.fullName}` : 'ID card only available on registration day'}
                            arrow
                          >
                            <span>   {/* span needed so Tooltip works on disabled button */}
                              <IconButton
                                onClick={() => canPrintID && handleOpenIDCard(patient)}
                                disabled={!canPrintID}
                                sx={{
                                  backgroundColor: canPrintID ? '#f0fdf4' : '#f8fafc',
                                  color: canPrintID ? '#16a34a' : '#cbd5e1',
                                  '&:hover': canPrintID ? {
                                    backgroundColor: '#dcfce7',
                                    color: '#15803d',
                                  } : {},
                                  '&:disabled': {
                                    backgroundColor: '#f8fafc',
                                    color: '#cbd5e1',
                                  },
                                  borderRadius: 2, p: 1,
                                  transition: 'all 0.2s',
                                }}
                              >
                                <BadgeIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>

                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ p: 3, backgroundColor: '#f8fafc', borderRadius: '0 0 8px 8px' }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={16} sx={{ color: '#059669' }} />
              <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>Loading patient records...</Typography>
            </Box>
          ) : (
            <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
              Showing {allPatientData.length} of {totalRecords} total entries
              {selectedPatients.size > 0 && (
                <Chip label={`${selectedPatients.size} selected`} size="small" sx={{ ml: 2, backgroundColor: '#059669', color: 'white' }} />
              )}
              {activeFilter !== 'today' && (
                <Chip label={`Filtered by: ${activeFilter}`} size="small" sx={{ ml: 2, backgroundColor: '#3b82f6', color: 'white' }} />
              )}
            </Typography>
          )}
        </Box>
      </Card>

      {/* Vitals Dialog */}
      <PatientVitals open={openPatient} onClose={handleClosePatient} info={vitalsInfo} />

      {/* ── ID Card Dialog ─────────────────────────────────────────────────── */}
      <PatientIDCard
        open={openIDCard}
        onClose={handleCloseIDCard}
        patient={idCardPatient}
        companyInfo={companyInfo}
      />
    </Box>
  );
};

export default Home;